import {
    FlashBlockType,
    FlowAnchor,
    FlowBlock,
    FlowBlockContext,
    FlowPoint,
    FlowPort,
    FlowPortKind,
    FlowSize,
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {FlowUtil} from '@mintdata-wsp/common/util/FlowUtil';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {FlowCanvasController} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowCanvasController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {FlowPortView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/canvas/FlowPortView';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, IReactionDisposer, observable, reaction} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./FlowBlockView.less');
const cx = classnames.bind(styles);

export interface FlowBlockViewProps
{
    flowId:number;
    block:FlowBlock;
    // isSelected is passed via props for performance gain. Otherwise each block is re-rendered after selection changes
    isSelected:boolean;
}

@observer
export class FlowBlockView extends React.Component<FlowBlockViewProps, any>
{
    private _isPreventDragDueToActiveWireDrag:boolean = false;
    private _blockRef:HTMLElement | SVGElement;
    private _instanceNameWatchDisposer:IReactionDisposer = null;

    @observable
    private _measureRequested:boolean = false;

    constructor(props:FlowBlockViewProps)
    {
        super(props);
        this._watchInstanceName();
    }

    @bind
    private _watchInstanceName():void
    {
        this._instanceNameWatchDisposer = reaction(() =>
            {
                return this.props.block.instanceName;
            },
            () =>
            {
                // Note: setTimeout() is here because the sequence of events is:
                // 1) block.instanceName is changed
                // 2) mobx fires this reaction
                // 3) on the next React render cycle the width of this FlowBlock actually changes
                // 4) we need to measure the DOM node's width only after item (3) in this list
                setTimeout(this._measureAndUpdateBlockSize, 0);
            });
    }

    @bind
    private _onBlockRef(ref:HTMLElement)
    {
        this._blockRef = ref;
    }

    @action.bound
    private _measureAndUpdateBlockSize()
    {
        const ref = this._blockRef;
        if( ref )
        {
            const boundingRect = ref.getBoundingClientRect();
            const width = boundingRect.width;
            const height = boundingRect.height;

            const {block} = this.props;
            const bounds = block.bounds;
            const newSize:FlowSize = new FlowSize(width, height);

            if( bounds.size && !bounds.size.isEqual(newSize) )
                bounds.size = newSize;
            // console.log(`Set flow block bounds.size to ${bounds.size}`);
        }
    }

    public componentDidUpdate():void
    {
        const {block} = this.props;
        const controller = controllers.ui.flowSelection;

        const shouldFlashBlock = controller.isBlockToFlash(block.id);

        if( shouldFlashBlock )
        {
            controller.setDoneFlashingBlock(550);
        }
    }

    public componentDidMount():void
    {
        this._measureAndUpdateBlockSize();
    }

    public componentWillUnmount():void
    {
        if( this._instanceNameWatchDisposer )
        {
            this._instanceNameWatchDisposer();
            this._instanceNameWatchDisposer = null;
        }
    }

    @bind
    private _onPortMouseDown(e:React.MouseEvent<any>):void
    {
        this._isPreventDragDueToActiveWireDrag = true;
    }

    @bind
    private _onPortMouseUpOrOut(e:React.MouseEvent<any>):void
    {
        this._isPreventDragDueToActiveWireDrag = false;
    }

    @bind
    private _onDragStart(reactEvent:any, data:DraggableData):any
    {
        const isAltKeyDown = (reactEvent as React.MouseEvent<any>).altKey;

        if( isAltKeyDown )
        {
            const {block} = this.props;
            const controller = controllers.ui.flowCanvas;

            controller.cloneAndSelectNewBlock(block);
        }

        return !this._isPreventDragDueToActiveWireDrag;
    }

    @bind
    private _onDragInProgress(e:MouseEvent, data:DraggableData):void
    {
        this._handleDragProgressOrStop(data, false);
    }

    @bind
    private _onDragStop(e:MouseEvent, data:DraggableData):void
    {
        this._handleDragProgressOrStop(data, false);
    }

    @bind
    private _handleDragProgressOrStop(data:DraggableData, shouldSnapPoint:boolean):void
    {
        const moveDelta = new FlowPoint(data.deltaX, data.deltaY);
        controllers.ui.flowCanvas.moveSelectedBlocksBy(moveDelta, shouldSnapPoint);
    }

    @bind
    private _onFlowBlockMouseDown(reactEvent:any):void
    {
        const controller = controllers.ui.flowCanvas;
        const selectionController = controllers.ui.flowSelection;
        const {block} = this.props;

        const isBoxSelected = selectionController.isBlockSelected(block);
        if( isBoxSelected )
        {
            if( reactEvent.shiftKey )
                selectionController.removeBlockFromSelection(block);
        }
        else
        {
            if( reactEvent.shiftKey )
                selectionController.addBlockToSelection(block);
            else
                selectionController.setSingleBlockSelection(block);
        }

        const isLeftClick = reactEvent.nativeEvent.which === 3;
        if( isLeftClick )
        {
            const e = reactEvent.nativeEvent;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            controller.openBlockContextMenu(e, block);
        }
    }

    @bind
    private _onBlockContextMenu(reactEvent:any)
    {
        const controller = controllers.ui.flowCanvas;
        const {block} = this.props;

        const e = reactEvent.nativeEvent;
        if( e.which === 3 )
        {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            controller.openBlockContextMenu(e, block);
        }
    }

    @bind
    private _onBlockDoubleClick(reactEvent:any)
    {
        const {block} = this.props;
        controllers.ui.flowCanvas.showRenameBlockDialog(block);
    }

    _renderWithoutInstanceName()
    {
        const {block} = this.props;
        const {component} = block;
        const componentName = NameUtil.getDisplayName(component);

        return <div className={cx('withoutInstanceName')}>
            <div className={cx('componentName')}>{componentName}</div>
        </div>;
    }

    _renderWithInstanceName()
    {
        const {block} = this.props;
        const {component} = block;
        const componentName = NameUtil.getDisplayName(component);
        const instanceName = block.instanceName;

        return <Column className={cx('withInstanceName')}>
            <div className={cx('instanceName')}>{instanceName}</div>
            <div className={cx('componentName')}>{componentName}</div>

        </Column>;
    }

    @bind
    private _computeFlashAnimationClassNames():string | null
    {
        const {block} = this.props;
        const controller = controllers.ui.flowSelection;

        const shouldFlashBlock = controller.isBlockToFlash(block.id);

        if( shouldFlashBlock )
        {
            const blockToFlash = controller.getBlockToFlash();
            const flashType = blockToFlash.type;

            if( flashType === FlashBlockType.Info )
                return cx('flashAnimationInfo');
            else if( flashType === FlashBlockType.Warning )
                return cx('flashAnimationWarning');
            else if( flashType === FlashBlockType.Error )
                return cx('flashAnimationError');
        }

        return null;
    }

    @bind
    private _onMouseEnter(reactEvent:any):void
    {
        const {block} = this.props;
        const defaultInputAnchor = this._computeDefaultPortAnchorIfAny(block.inputPorts);
        const defaultOutputAnchor = this._computeDefaultPortAnchorIfAny(block.outputPorts);

        const ctx = new FlowBlockContext(block,
            defaultInputAnchor,
            defaultOutputAnchor);

        controllers.ui.flowCanvas.setMouseOverBlock(ctx);
    }

    @bind
    private _computeDefaultPortAnchorIfAny(ports:FlowPort[]):FlowAnchor | null
    {
        if( ports && ports.length === 1 )
        {
            const firstPort = ports[0];
            const {block} = this.props;
            // const portPosition = controllers.ui.flowCanvas.getAbsolutePortPosition(block, firstPort);

            return new FlowAnchor(block, firstPort);
        }

        return null;
    }

    @bind
    private _onMouseLeave(reactEvent:any):void
    {
        controllers.ui.flowCanvas.setMouseOverBlock(null);
    }

    private _getDefaultPortToHighlight():FlowPortKind | null
    {
        const controller = controllers.ui.flowCanvas;

        const activeWire = controller.getActiveWire();
        if( !activeWire )
            return null;

        const blockCtx = controller.getMouseOverBlock();
        if( blockCtx && blockCtx.flowBlock.id === this.props.block.id )
        {
            const port = activeWire.source.port ? activeWire.source.port : activeWire.target.port;
            const kind = port.kind;
            const kindToHighlight = kind === FlowPortKind.InputPort ? FlowPortKind.OutputPort : FlowPortKind.InputPort;

            const inputPorts = this.props.block.inputPorts;
            const outputPorts = this.props.block.outputPorts;

            if( kindToHighlight === FlowPortKind.InputPort )
                return inputPorts.length > 0 ? FlowPortKind.InputPort : null;

            if( kindToHighlight === FlowPortKind.OutputPort )
                return outputPorts.length > 0 ? FlowPortKind.OutputPort : null;
        }

        return null;
    }

    _renderInputPorts(kind:FlowPortKind)
    {
        const {flowId, block, isSelected} = this.props;
        const portToHighlightDuringActiveDrag:FlowPortKind | null = this._getDefaultPortToHighlight();
        const isHighlightPort = portToHighlightDuringActiveDrag && portToHighlightDuringActiveDrag === kind;
        const ports = (kind === FlowPortKind.InputPort) ? block.inputPorts : block.outputPorts;

        if( !ports || ports.length === 0 )
            return null;

        return ports.map((port:FlowPort) =>
        {
            const isValid = controllers.ui.flowValidation.isPortValid(flowId, port);

            return <FlowPortView
                key={port.id}
                isHighlighted={isHighlightPort}
                isSelected={isSelected}
                isValid={isValid}
                flowBlock={block}
                offset={FlowUtil.getPortTargetAreaOffsetFromBlock(block, port)}
                flowPort={port}
                onMouseDown={this._onPortMouseDown}
                onMouseUpOrOut={this._onPortMouseUpOrOut}
            />;
        });
    }

    render()
    {
        const {flowId, block, isSelected} = this.props;
        const {instanceName} = block;
        const position = block.bounds.position;
        const style = position.toCssStyle();
        const hasInstanceName = instanceName !== null && instanceName.length > 0;
        const isValid = controllers.ui.flowValidation.isBlockValid(flowId, block);
        const isVisualLink = !!block.visualLink;

        const portToHighlightDuringActiveDrag:FlowPortKind | null = this._getDefaultPortToHighlight();
        const iconElement = controllers.ui.componentIcons.getIconForBlock(block, WSPIconSize.xlarge_64);

        if( FlowCanvasController.HIDE_DEBUG_BLOCKS_AND_WIRES && block.isDebugBlock )
            return null;

        const classNames = this._computeFlashAnimationClassNames();

        return <DraggableCore onStart={this._onDragStart}
                              onDrag={this._onDragInProgress}
                              onStop={this._onDragStop}
                              onMouseDown={this._onFlowBlockMouseDown}
                              enableUserSelectHack={false}
        >
            <div className={cx('flowBlockView',
                {'regularFlowBlock': !isVisualLink},
                {'visualLinkFlowBlock': isVisualLink},
                {'selectedFlowBlock': isSelected},
                {'invalidFlowBlock': !isValid},
                {'blockDuringActiveWireDrag': !!portToHighlightDuringActiveDrag},
                classNames,
            )}
                 style={style}
                 onContextMenu={this._onBlockContextMenu}
                 onDoubleClick={this._onBlockDoubleClick}
                 onMouseEnter={this._onMouseEnter}
                 onMouseLeave={this._onMouseLeave}
                 ref={this._onBlockRef}
            >

                {
                    this._renderInputPorts(FlowPortKind.InputPort)
                }

                {
                    iconElement ?
                        iconElement
                        :
                        <WSPIcon className={cx('icon')} name={WSPIcons.visualDesigner.components.Gears}
                                 size={WSPIconSize.large} />
                }

                {
                    hasInstanceName ?
                        this._renderWithInstanceName()
                        :
                        this._renderWithoutInstanceName()
                }

                {
                    this._renderInputPorts(FlowPortKind.OutputPort)
                }
            </div>
        </DraggableCore>;
    }
}
